/** @file Prefixed ULID ids (`bone_01J…`): 10 time chars + 16 random chars, Crockford base32. */

const PREFIXES = ['proj', 'bone', 'slot', 'att', 'anim', 'skin', 'ik', 'evt', 'img', 'sock', 'ref'] as const;
export type IdPrefix = (typeof PREFIXES)[number];
export type Id = string;

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const BODY = '[0-9A-HJKMNP-TV-Z]{26}';
const PREFIX_RE = Object.fromEntries(PREFIXES.map((p) => [p, new RegExp(`^${p}_${BODY}$`)])) as Record<IdPrefix, RegExp>;

function encodeTime(now: number): string {
  let t = Math.floor(now);
  let out = '';
  for (let i = 0; i < 10; i++) {
    out = (ALPHABET[t % 32] ?? '0') + out;
    t = Math.floor(t / 32);
  }
  return out;
}

function encodeRandom(rng: () => number): string {
  let out = '';
  for (let i = 0; i < 16; i++) out += ALPHABET[Math.floor(rng() * 32) % 32] ?? '0';
  return out;
}

export const ids = {
  /** Matches any prefixed id. */
  RE: new RegExp(`^(${PREFIXES.join('|')})_${BODY}$`),
  PREFIXES,
  /** Matches ids of one prefix only (`ids.re('bone')` rejects a slot id). */
  re(prefix: IdPrefix): RegExp {
    return PREFIX_RE[prefix];
  },
  /** Creates `<prefix>_<ULID>`; `rng` returns [0,1) and `now` is a millisecond timestamp (both injected for determinism). */
  create(prefix: IdPrefix, rng: () => number, now: number): Id {
    return `${prefix}_${encodeTime(now)}${encodeRandom(rng)}`;
  },
  /** True for any well-formed prefixed id. */
  isId(value: string): boolean {
    return ids.RE.test(value);
  },
  /** The prefix of a well-formed id, or null. */
  prefixOf(value: string): IdPrefix | null {
    const m = ids.RE.exec(value);
    return m ? (m[1] as IdPrefix) : null;
  },
};
